import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/index';
import { ResponseList, RoutePathParts, SelectedSearchModel } from '../../types';
import { ConfigService } from './config.service';
import { CacheService } from './cache.service';

export interface IBaseRequestService {
  pagedOffsetGet<T>(route: RoutePathParts, page?: number): Observable<ResponseList<T>>;

  prepareUrl(path: RoutePathParts): string;
}

@Injectable({ providedIn: 'root' })
export class BaseRequestService implements IBaseRequestService {
  http: HttpClient = inject(HttpClient);
  #router: Router = inject(Router);
  configService: ConfigService = inject(ConfigService);
  cacheService = inject(CacheService);

  pagedGet<T>(
    route: RoutePathParts,
    page?: number,
    searchParams: SelectedSearchModel[] = []
  ): Observable<ResponseList<T>> {
    let params = this.prepareHttpParams(searchParams);
    if (page > 1) {
      params = params.append('page', page);
    }
    return this.http.get<ResponseList<T>>(this.prepareUrl(route), { params });
  }

  pagedOffsetGet<T>(
    route: RoutePathParts,
    page?: number,
    searchParams: SelectedSearchModel[] = [],
    additionalParams: Record<string, string> = {},
    isExternalRoute = false
  ): Observable<ResponseList<T>> {
    let params = this.prepareHttpParams(searchParams);

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params = params.append(key, value);
      });
    }

    if (page !== undefined) {
      params = params.append('page', page ?? 1);
    }

    return this.http.get<ResponseList<T>>(this.prepareUrl(route, isExternalRoute), { params });
  }

  pagedCursorGet<T>(
    route: RoutePathParts,
    cursor?: string,
    searchParams: SelectedSearchModel[] = [],
    isExternalRoute = false
  ): Observable<ResponseList<T>> {
    let params = this.prepareHttpParams(searchParams);
    if (cursor) {
      params = params.append('cursor', cursor);
    }
    return this.http.get<ResponseList<T>>(this.prepareUrl(route, isExternalRoute), { params });
  }

  prepareUrl(path: RoutePathParts, isExternalRoute = false): string {
    if (this.isAbsoluteUrl(path)) {
      const url = path.join('/');
      return url.replace(/\/+$/, '') + '/';
    }

    const route = this.configService.config.requestEndSlash ? [...path] : [...path, ''];

    if (isExternalRoute) return route.join('/');

    const url = this.#router.createUrlTree(route);
    return url.toString();
  }

  isAbsoluteUrl(path: RoutePathParts): boolean {
    return (
      path.length > 0 &&
      typeof path[0] === 'string' &&
      (path[0].startsWith('http://') || path[0].startsWith('https://'))
    );
  }

  prepareHttpParams(searchParams: SelectedSearchModel[] = []): HttpParams {
    let params = new HttpParams();
    if (searchParams && searchParams.length) {
      const searchTerms: string[] = [];
      searchParams.forEach(item => {
        if (item.type === 'filter_preset') {
          const hasPresets = params.get('filter_preset');
          let presetsArray = new Array<string>();
          if (hasPresets) {
            params = params.delete('filter_preset');
            presetsArray = hasPresets.split(',');
          }
          presetsArray.push(item.id);
          params = params.append('filter_preset', presetsArray.join(','));
        } else if (item.type === 'custom') {
          params = params.append(item.id, item.title);
        } else {
          params = params.append(item.type, item.id);
        }
      });
      if (searchTerms.length) {
        params = params.append('search', searchTerms.join(' '));
      }
    }

    return params;
  }
}
